import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Container,
  Collapse,
} from '@mui/material';
import { Download as DownloadIcon, Upload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'notistack';
import api from '../api';
import { formatErrorMessage } from '../utils';
import { toast } from 'react-hot-toast';
import { getConfig } from '../config';

//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { API_BASE_URL } = getConfig();
interface SaleItem {
  id: number;
  sale_id: number;
  item: string;
  amount: number;
  state: string;
  release_date: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  completion_date: string | null;
  purpose: string | null;
  material_assignment_id: number | null;
  design_file: string | null;
  design_file_name: string | null;
  structure_file: string | null;
  structure_file_name: string | null;
  production_file: string | null;
  production_file_name: string | null;
  sale?: {
    id: number;
    contract_date: string;
    company_id: number;
    site_id: number;
    estimate_file: string | null;
    created_at: string;
    company: {
      id: number;
      name: string;
    };
    site: {
      id: number;
      name: string;
    };
  };
}

interface Sale {
  id: number;
  contract_date: string;
  total_amount: number;
  estimate_file?: string;
  created_at: string;
  company: {
    id: number;
    name: string;
  };
  site: {
    id: number;
    name: string;
  };
  items: SaleItem[];
}

interface FileInfo {
  name: string;
  type: string;
  path: string;
  originalName: string;
}

const stateOptions = [
  { value: '대기', label: '대기' },
  { value: '생산', label: '생산' },
  { value: '생산완료', label: '생산완료' },
  { value: '입고', label: '입고' },
  { value: '출고', label: '출고' },
  { value: '계산서발행', label: '계산서발행' },
];

interface SiteGroup {
  site: {
    id: number;
    name: string;
  };
  sales: SaleItem[];
}

interface CompanyGroup {
  company: {
    id: number;
    name: string;
  };
  sites: {
    [key: number]: SiteGroup;
  };
}

interface GroupedSales {
  [key: number]: CompanyGroup;
}

const Designs: React.FC = () => {
  const [sales, setSales] = useState<SaleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<SaleItem | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File | null }>({
    design: null,
    structure: null,
    production: null
  });
  const [fileList, setFileList] = useState<FileInfo[]>([]);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(new Set());
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set());
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchSales();
  }, []);
  const fetchSales = async () => {
    const token = localStorage.getItem("token");
    console.log("🔥 토큰 확인:", token);

    if (!token) {
      window.location.href = "/login";
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/sale-items`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      if (!response.ok) {
        throw new Error("매출 목록을 가져오는데 실패했습니다.");
      }

      const data = await response.json();
      const filtered = data.filter((item: any) => item.sale?.company && item.sale?.site);
      console.log("🔍 전체 데이터:", data);
      console.log("✅ 필터 후:", filtered);
      setSales(filtered);
    } catch (error) {
      console.error("매출 목록 조회 중 오류 발생:", error);
      toast.error("매출 목록을 가져오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };
  const handleDownloadFile = async (
    filePath: string,
    fileName: string,
    saleId: number,
    fileType: string
  ): Promise<void> => {
    try {
      // 🔥 1단계: presigned URL 먼저 받아오기
      const { data } = await api.get(`/api/sale-items/${saleId}/files/${fileType}`);

      const presignedUrl = data.url;

      // 🔥 2단계: 브라우저가 직접 다운로드하게 처리
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.setAttribute('download', fileName); // 🔥 filename 명시
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('파일 다운로드 중 오류 발생:', error);
      toast.error(error instanceof Error ? error.message : '파일 다운로드에 실패했습니다.');
    }
  };


  const handleUploadClick = (sale: SaleItem) => {
    setSelectedSale(sale);
    setUploadDialogOpen(true);
    // 파일 리스트 초기화
    const files: FileInfo[] = [];
    if (sale.design_file) files.push({ 
      name: '도면', 
      type: 'design', 
      path: sale.design_file,
      originalName: sale.design_file_name || '도면.pdf'
    });
    if (sale.structure_file) files.push({ 
      name: '구조검토서', 
      type: 'structure', 
      path: sale.structure_file,
      originalName: sale.structure_file_name || '구조검토서.pdf'
    });
    if (sale.production_file) files.push({ 
      name: '제작도면', 
      type: 'production', 
      path: sale.production_file,
      originalName: sale.production_file_name || '제작도면.pdf'
    });
    setFileList(files);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, fileType: string) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFiles(prev => ({
        ...prev,
        [fileType]: event.target.files![0]
      }));
    }
  };

  const handleUpload = async () => {
    try {
      if (!selectedSale) return;

      // 업로드할 파일이 있는지 확인
      const hasFilesToUpload = Object.values(selectedFiles).some(file => file !== null);
      if (!hasFilesToUpload) {
        toast.error('업로드할 파일을 선택해주세요.');
        return;
      }

      // 각 파일 타입별로 업로드
      const updatedSale = { ...selectedSale };

      for (const [type, file] of Object.entries(selectedFiles)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);

          const response = await fetch(`${API_BASE_URL}/api/sale-items/${selectedSale.id}/files/${type}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });

          if (!response.ok) {
            throw new Error('파일 업로드에 실패했습니다.');
          }

          const result = await response.json();

          // 업로드된 파일 정보 업데이트
          if (type === 'design') {
            updatedSale.design_file = result.file_path;
            updatedSale.design_file_name = result.file_name;
          } else if (type === 'structure') {
            updatedSale.structure_file = result.file_path;
            updatedSale.structure_file_name = result.file_name;
          } else if (type === 'production') {
            updatedSale.production_file = result.file_path;
            updatedSale.production_file_name = result.file_name;
          }

          // 파일 리스트 업데이트
          setFileList(prev => {
            const newFile = {
              name: getFileTypeLabel(type),
              type,
              path: result.file_path,
              originalName: result.file_name
            };
            return [...prev.filter(f => f.type !== type), newFile];
          });
        }
      }

      // 전체 sales 상태 업데이트
      setSales(prevSales =>
        prevSales.map(sale =>
          sale.id === updatedSale.id ? updatedSale : sale
        )
      );

      setUploadDialogOpen(false);
      setSelectedFiles({
        design: null,
        structure: null,
        production: null
      });
      toast.success('파일이 성공적으로 업로드되었습니다.');
    } catch (error) {
      console.error('파일 업로드 중 오류 발생:', error);
      toast.error('파일 업로드에 실패했습니다.');
    }
  };

  const handleDeleteAllFiles = async () => {
    try {
      if (!selectedSale) return;

      const response = await fetch(`${API_BASE_URL}/api/sale-items/${selectedSale.id}/files/all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('파일 삭제에 실패했습니다.');
      }

      // sales 상태 업데이트
      setSales(prevSales =>
        prevSales.map(sale =>
          sale.id === selectedSale.id
            ? {
                ...sale,
                design_file: null,
                design_file_name: null,
                structure_file: null,
                structure_file_name: null,
                production_file: null,
                production_file_name: null
              } as SaleItem
            : sale
        )
      );

      // 파일 리스트 초기화
      setFileList([]);
      toast.success('모든 파일이 삭제되었습니다.');
    } catch (error) {
      console.error('파일 삭제 중 오류 발생:', error);
      toast.error('파일 삭제에 실패했습니다.');
    }
  };

  const getFileTypeLabel = (type: string) => {
    switch (type) {
      case 'design':
        return '도면';
      case 'structure':
        return '구조검토서';
      case 'production':
        return '제작도면';
      default:
        return '';
    }
  };

  const handleEdit = async (item: SaleItem, changes: Partial<SaleItem>) => {
    try {
      const updateData: any = {};
      
      if ('state' in changes) {
        updateData.state = changes.state;
      }
      
      if ('release_date' in changes) {
        updateData.release_date = changes.release_date;
      }

      await api.put(`/api/sale-items/${item.id}`, updateData);
      enqueueSnackbar('항목 정보가 수정되었습니다.', { variant: 'success' });
      fetchSales();
    } catch (error) {
      console.error('항목 수정 오류:', error);
      enqueueSnackbar('항목 수정에 실패했습니다.', { variant: 'error' });
    }
  };

  const toggleCompany = (companyId: number) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  const toggleSite = (siteId: number) => {
    const newExpanded = new Set(expandedSites);
    if (newExpanded.has(siteId)) {
      newExpanded.delete(siteId);
    } else {
      newExpanded.add(siteId);
    }
    setExpandedSites(newExpanded);
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case '대기':
        return 'default';
      case '생산':
        return 'warning';
      case '생산완료':
        return 'info';
      case '입고':
        return 'warning';
      case '출고':
        return 'success';
      case '계산서발행':
        return 'primary';
      default:
        return 'default';
    }
  };

  // 업체별로 매출 데이터 그룹화
  const groupedSales = sales.reduce((acc: GroupedSales, saleItem) => {
    if (!saleItem.sale?.company || !saleItem.sale?.site) {
      console.warn('Invalid sale data:', saleItem);
      return acc;
    }

    const company = saleItem.sale.company;
    const site = saleItem.sale.site;

    if (!acc[company.id]) {
      acc[company.id] = {
        company: company,
        sites: {},
      };
    }
    if (!acc[company.id].sites[site.id]) {
      acc[company.id].sites[site.id] = {
        site: site,
        sales: [],
      };
    }
    acc[company.id].sites[site.id].sales.push(saleItem);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          도면 관리
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {Object.values(groupedSales).map(({ company, sites }) => (
              <Box key={company.id} sx={{ mb: 2 }}>
                <Button
                  startIcon={expandedCompanies.has(company.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => toggleCompany(company.id)}
                  sx={{ 
                    width: '100%', 
                    justifyContent: 'flex-start',
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                    },
                  }}
                >
                  <Typography variant="h6">{company.name}</Typography>
                </Button>
                <Collapse in={expandedCompanies.has(company.id)}>
                  <Box sx={{ ml: 2 }}>
                    {(Object.values(sites) as SiteGroup[]).map(({ site, sales }) => (
                      <Box key={site.id} sx={{ mb: 2 }}>
                        <Button
                          startIcon={expandedSites.has(site.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          onClick={() => toggleSite(site.id)}
                          sx={{ 
                            width: '100%', 
                            justifyContent: 'flex-start',
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f0f0f0',
                            },
                          }}
                        >
                          <Typography variant="subtitle1">{site.name}</Typography>
                        </Button>
                        <Collapse in={expandedSites.has(site.id)}>
                          <TableContainer sx={{ ml: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell width="30%">항목</TableCell>
                                  <TableCell align="center">도면</TableCell>
                                  <TableCell align="center">구조검토서</TableCell>
                                  <TableCell align="center">제작도면</TableCell>
                                  <TableCell align="center" width={100}></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sales.map((sale: SaleItem) => (
                                  <TableRow key={sale.id}>
                                    <TableCell>{sale.item}</TableCell>
                                    <TableCell align="center">
                                      {sale.design_file ? (
                                        <IconButton
                                          onClick={() => handleDownloadFile(sale.design_file!, sale.design_file_name || '도면.pdf', sale.id, 'design')}
                                          size="small"
                                          color="primary"
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          onClick={() => handleUploadClick(sale)}
                                          size="small"
                                          color="primary"
                                        >
                                          <UploadIcon />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {sale.structure_file ? (
                                        <IconButton
                                          onClick={() => handleDownloadFile(sale.structure_file!, sale.structure_file_name || '구조검토서.pdf', sale.id, 'structure')}
                                          size="small"
                                          color="primary"
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          onClick={() => handleUploadClick(sale)}
                                          size="small"
                                          color="primary"
                                        >
                                          <UploadIcon />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {sale.production_file ? (
                                        <IconButton
                                          onClick={() => handleDownloadFile(sale.production_file!, sale.production_file_name || '제작도면.pdf', sale.id, 'production')}
                                          size="small"
                                          color="primary"
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          onClick={() => handleUploadClick(sale)}
                                          size="small"
                                          color="primary"
                                        >
                                          <UploadIcon />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Button
                                        onClick={() => handleUploadClick(sale)}
                                        variant="outlined"
                                        size="small"
                                        startIcon={<UploadIcon />}
                                        sx={{ 
                                          minWidth: '100px',
                                          height: '32px',
                                          textTransform: 'none',
                                          fontSize: '0.875rem'
                                        }}
                                      >
                                        도면관리
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        disablePortal
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>
          {selectedSale?.sale && `${selectedSale.sale.company.name} - ${selectedSale.sale.site.name}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                파일 업로드
              </Typography>
              {fileList.length > 0 && (
                <Button
                  onClick={handleDeleteAllFiles}
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                >
                  전체 삭제
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2">도면</Typography>
                  {fileList.find(f => f.type === 'design') && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {fileList.find(f => f.type === 'design')?.originalName}
                      </Typography>
                      <IconButton
                        onClick={() => handleDownloadFile(
                          fileList.find(f => f.type === 'design')?.path || '',
                          fileList.find(f => f.type === 'design')?.originalName || '',
                          selectedSale?.id || 0,
                          'design'
                        )}
                        size="small"
                        color="primary"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(e, 'design')}
                  accept="*"
                  style={{ marginTop: '5px' }}
                  aria-label="도면 파일 선택"
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2">구조검토서</Typography>
                  {fileList.find(f => f.type === 'structure') && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {fileList.find(f => f.type === 'structure')?.originalName}
                      </Typography>
                      <IconButton
                        onClick={() => handleDownloadFile(
                          fileList.find(f => f.type === 'structure')?.path || '',
                          fileList.find(f => f.type === 'structure')?.originalName || '',
                          selectedSale?.id || 0,
                          'structure'
                        )}
                        size="small"
                        color="primary"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(e, 'structure')}
                  accept="*"
                  style={{ marginTop: '5px' }}
                  aria-label="구조검토서 파일 선택"
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2">제작도면</Typography>
                  {fileList.find(f => f.type === 'production') && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {fileList.find(f => f.type === 'production')?.originalName}
                      </Typography>
                      <IconButton
                        onClick={() => handleDownloadFile(
                          fileList.find(f => f.type === 'production')?.path || '',
                          fileList.find(f => f.type === 'production')?.originalName || '',
                          selectedSale?.id || 0,
                          'production'
                        )}
                        size="small"
                        color="primary"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(e, 'production')}
                  accept="*"
                  style={{ marginTop: '5px' }}
                  aria-label="제작도면 파일 선택"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>취소</Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            disabled={!Object.values(selectedFiles).some(file => file !== null)}
          >
            업로드
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Designs; 